import React from 'react';
import { Translation } from '../translations';

interface FeaturesSectionProps {
  t: Translation;
}

const FeaturesSection: React.FC<FeaturesSectionProps> = ({ t }) => {
  // Custom SVG icon for video calls
  const VideoCallIcon = () => (
    <svg viewBox="0 0 64 64" className="w-8 h-8" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Left screen/person */}
      <rect x="4" y="16" width="26" height="32" rx="3" fill="#E0E7FF" stroke="#6366F1" strokeWidth="2"/>
      <rect x="7" y="19" width="20" height="18" rx="2" fill="#6366F1" opacity="0.1"/>
      {/* Person 1 */}
      <circle cx="17" cy="26" r="4" fill="#6366F1"/>
      <path d="M17 31c-4 0-7 2-7 5v1h14v-1c0-3-3-5-7-5z" fill="#6366F1"/>
      
      {/* Right screen/person */}
      <rect x="34" y="16" width="26" height="32" rx="3" fill="#FEF3C7" stroke="#F59E0B" strokeWidth="2"/>
      <rect x="37" y="19" width="20" height="18" rx="2" fill="#F59E0B" opacity="0.1"/>
      {/* Person 2 */}
      <circle cx="47" cy="26" r="4" fill="#F59E0B"/>
      <path d="M47 31c-4 0-7 2-7 5v1h14v-1c0-3-3-5-7-5z" fill="#F59E0B"/>
      
      {/* Connection lines */}
      <path d="M30 28L34 28" stroke="#10B981" strokeWidth="2" strokeDasharray="2 2" opacity="0.6"/>
      <path d="M30 32L34 32" stroke="#10B981" strokeWidth="2" strokeDasharray="2 2" opacity="0.6"/>
      <path d="M30 36L34 36" stroke="#10B981" strokeWidth="2" strokeDasharray="2 2" opacity="0.6"/>
      
      {/* Camera indicators */}
      <circle cx="17" cy="42" r="2" fill="#EF4444"/>
      <circle cx="47" cy="42" r="2" fill="#10B981"/>
      
      {/* Audio wave effects */}
      <path d="M8 28Q6 28 6 26T8 24" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
      <path d="M26 28Q28 28 28 26T26 24" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
      <path d="M38 28Q36 28 36 26T38 24" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
      <path d="M56 28Q58 28 58 26T56 24" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
    </svg>
  );

  return (
    <section id="features" className="py-24 md:py-32 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-20 left-0 w-72 h-72 bg-primary-100 rounded-full blur-3xl opacity-20"></div>
      <div className="absolute bottom-20 right-0 w-96 h-96 bg-secondary-100 rounded-full blur-3xl opacity-20"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <div className="inline-flex items-center px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold mb-4">
            <span className="mr-2">✨</span>
            Premium Features
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-6">
            {t.features.title}
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto">
            {t.features.subtitle}
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {t.features.items.map((feature, index) => (
            <div 
              key={index}
              className="group relative bg-white rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
            >
              {/* Gradient overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  {/* Use custom SVG for video call feature, emoji for others */}
                  {feature.icon === '📹' ? (
                    <VideoCallIcon />
                  ) : (
                    <span className="text-3xl">{feature.icon}</span>
                  )}
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary-700 transition-colors duration-300 break-words">
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed break-words">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;